import * as Y from 'yjs'
import { WebsocketProvider } from 'y-websocket'
import CodeMirror from 'codemirror'
import groupBy from 'lodash/groupBy'
import { Classroom as ClassroomConstants } from '@constants'
import { CodeMirrorBinding, QuillBinding, FabricBinding } from '../bindings'

import { RecordingSocketProvider } from '../../components/Recording/helpers'
import ObserverProxy from './ObserverProxy'

class ClassroomManager {
  constructor() {
    this.doc = null
    this.provider = null
    this.panels = null
    this.frames = null
    this.files = null
    this.editors = {}

    window.cm = this
  }

  initYDoc = (env) => {
    this.doc = new Y.Doc()

    this.panels = this.doc.getArray('panels')
    this.frames = this.doc.getMap('frames')
    this.files = this.doc.getMap('files')
    this.fullscreen = this.doc.getText('fullscreen')

    switch (env) {
      case ClassroomConstants.ENV.RECORDING: {
        this.participants = this.doc.getMap('participants')
        break
      }
      case ClassroomConstants.ENV.SKETCHPAD: {
        break
      }
      default: {
        this.initParticipant()
      }
    }

    return this.doc
  }

  init = ({ id, user, env = ClassroomConstants.ENV.CLASSROOM, token }) => {
    this.env = env
    this.classroomId = id
    this.token = token
    this.user = {
      id: user.id,
      name: user.name,
      image: user.image,
      type: user.type
    }

    this.initYDoc(env)
    this.editors = {}
    this.observers = {}
    this.observerProxy = ObserverProxy.create()

    this.provider = this.createWebsocketProvider(
      this.getSocketUrl('root'),
      id,
      this.doc
    )
    this.synced = env === ClassroomConstants.ENV.RECORDING
    this.initProvider(this.provider, id)
  }

  initFrame = (type) => {
    const frame = new Y.Map()
    frame.set('tabs', new Y.Array())
    frame.set('active', '')
    frame.set('fullscreen', false)
    this.frames.set(type, frame)
  }

  initProvider = (provider, id) => {
    provider.id = id

    provider.awareness.setLocalStateField('user', {
      name: this.user.name,
      image: this.user.image,
      type: this.user.type,
      id: this.user.id
    })

    if (id !== this.classroomId) return

    /** Only initialize frames if they're not initialized already */
    const onSynced = (isSynced) => {
      if (!isSynced) return

      const frames = ['sketch', 'note', 'code', 'finder'].filter(
        (frame) => !this.frames.get(frame)
      )

      this.doc.transact(() => {
        frames.forEach((frame) => {
          this.initFrame(frame)
        })
      })

      provider.off('sync', onSynced)
      this.synced = true
    }

    provider.on('sync', onSynced)
  }

  /**
   * Initializes participant related state for local participant.
   */
  initParticipant = () => {
    const participants = this.doc.getMap('participants')

    participants.set(this.user.id, {
      audio: false,
      video: false,
      screen: false
    })

    this.participants = participants
  }

  updateParticipant = (update) => {
    const userId = this.user.id
    const prevState = this.participants.get(userId)
    const state = {
      ...prevState,
      ...update
    }

    this.participants.set(userId, state)
  }

  // Currently redundant as it gets handled on the back-end
  destroyParticipant = () => {}

  destroy = () => {
    // TODO
    // We should reset everything to initial values here
    this.destroyProvider(this.provider)
    this.destroyParticipant()
    if (this.editors.sketch?.provider)
      this.destroyProvider(this.editors.sketch.provider)
    if (this.editors.note?.provider)
      this.destroyProvider(this.editors.note.provider)
    if (this.editors.code?.provider)
      this.destroyProvider(this.editors.code.provider)
  }

  destroyProvider = (provider) => {
    provider.awareness.setLocalState(null)
    provider.destroy()
  }

  attachEditor = (type, editor) => {
    this.log(`attachEditor(${type}, ${editor})`)
    if (this.editors[type]) {
      this.editors[type] = {
        ...this.editors[type],
        container: editor
      }
      return
    }

    this.editors[type] = { container: editor, binding: null, provider: null }
  }

  openPanel = (panel) => {
    this.panels.push([panel])
  }

  closePanel = (panel) => {
    this.panels.delete(
      this.panels.toArray().findIndex((p) => p === panel),
      1
    )
  }

  togglePanel = (panel) => {
    if (this.panels.toArray().includes(panel)) this.closePanel(panel)
    else this.openPanel(panel)
  }

  isPanelOpen = (panel) => this.panels.toArray().includes(panel)

  closePanels = () => {
    this.doc.transact(() => {
      this.panels.toArray().forEach(this.closePanel)
    })
  }

  updatePanels = () => {
    const panels = this.panels.toArray()
    return ['sketch', 'code', 'note']
      .filter((panel) => !panels.includes(panel))
      .forEach((panel) => {
        const editor = this.editors[panel]
        if (!editor) return
        if (editor.provider) {
          this.destroyProvider(editor.provider)
          editor.provider = null
        }
        if (editor.binding) {
          editor.binding.destroy()
          editor.binding = null
        }
      })
  }

  sortPanels = (panels) => {
    return panels.sort((a, b) =>
      a === 'sketch'
        ? -1
        : a === 'note' && b !== 'sketch'
        ? -1
        : a === 'code' && b !== 'sketch' && b !== 'note'
        ? -1
        : 1
    )
  }

  renameFile = (file) => {
    this.frames
      .get(file.type)
      .get('tabs')
      .forEach((v) => {
        if (v.get('id') === file.id) {
          v.set('name', file.name)
        }
      })
  }

  updateMetadata = (file) => {
    const metadata = this.files.get(file.id).get('metadata')
    for (let [key, value] of metadata) {
      if (!file.metadata[key]) continue
      if (file.metadata[key] !== value) metadata.set(key, file.metadata[key])
    }
  }

  updateFile = (file) => {
    const tab = this.frames
      .get(file.type)
      .get('tabs')
      .toArray()
      .find((v) => v.get('id') === file.id)
    if (!tab) return
    if (tab.get('name') !== file.name) this.renameFile(file)
    if (file.metadata) this.updateMetadata(file)
  }

  openFile = (file) => {
    this.log(`openFile${JSON.stringify(file)}`)
    let tab = this.frames
      .get(file.type)
      .get('tabs')
      .toArray()
      .find((tab) => tab.get('id') === file.id)
    if (!tab) {
      tab = new Y.Map()
      tab.set('name', file.name)
      tab.set('id', file.id)
      this.frames.get(file.type).get('tabs').push([tab])
    }
    tab.set('accessedAt', Date.now())
    this.activateFile(file)
  }

  closeFile = (file) => {
    if (file.type === 'finder')
      return this.activateFile({ type: file.type, id: '' })

    const tabs = this.frames.get(file.type).get('tabs').toJSON()
    const index = tabs.findIndex((tab) => tab.id === file.id)
    const tab = tabs.find((tab) => tab.id === file.id)

    if (tab.id === this.frames.get(file.type).get('active').toString()) {
      const next = tabs.reduce(
        (acc, v) =>
          v.accessedAt > acc.accessedAt && v.id !== tab.id ? v : acc,
        {
          accessedAt: 0
        }
      )
      this.activateFile({ type: file.type, id: next.id || '' })
    }

    this.frames.get(file.type).get('tabs').delete(index, 1)
  }

  activateFile = (file) => {
    this.log(`activateFile(${JSON.stringify(file)})`)
    this.frames.get(file.type).set('active', file.id)
    this.frames
      .get(file.type)
      .get('tabs')
      .forEach((v) => {
        if (v.get('id') === file.id) v.set('accessedAt', Date.now())
      })
  }

  loadFile = (file, observers) => {
    this.log(`loadFile(${JSON.stringify(file)})`)
    let editor = this.editors[file.type]
    if (!editor) {
      this.attachEditor(file.type, null)
      editor = this.editors[file.type]
    }

    if (editor?.provider) this.destroyProvider(editor.provider)
    if (editor?.binding) editor.binding.destroy()

    switch (file.type) {
      case 'code':
        this.loadCode(file.id, observers)
        break
      case 'note':
        this.loadNote(file.id)
        break
      case 'sketch':
        this.loadSketch(file.id)
        break
    }
  }

  loadSketch = (id) => {
    this.log(`loadSketch(${id})`)
    if (!this.files.has(id)) {
      const metadata = new Y.Map()
      const entry = new Y.Map()
      entry.set('metadata', metadata)
      this.files.set(id, entry)
    }

    const yDoc = new Y.Doc()
    const provider = this.createWebsocketProvider(
      this.getSocketUrl('sketch'),
      id,
      yDoc
    )
    this.initProvider(provider, id)
    const content = yDoc.getMap('')

    this.editors.sketch.provider = provider
    this.editors.sketch.binding = new FabricBinding(
      content,
      this.editors.sketch.container,
      provider.awareness,
      this.files.get(id).get('metadata'),
      this
    )
  }

  loadNote = (id) => {
    this.log(`loadNote(${id})`)
    if (!this.files.has(id)) {
      const metadata = new Y.Map()
      const entry = new Y.Map()
      entry.set('metadata', metadata)
      this.files.set(id, entry)
    }

    const yDoc = new Y.Doc()
    const provider = this.createWebsocketProvider(
      this.getSocketUrl('note'),
      id,
      yDoc
    )
    this.initProvider(provider, id)

    const content = yDoc.getText()

    this.editors.note.container.history.clear()
    this.editors.note.provider = provider
    this.editors.note.binding = new QuillBinding(
      content,
      this.editors.note.container,
      provider.awareness
    )
  }

  loadCode = (id, observers) => {
    this.log(`loadCode(${id})`)
    if (!this.files.has(id)) {
      const mode = 'javascript'
      const metadata = new Y.Map()
      const entry = new Y.Map()

      metadata.set('mode', mode)
      entry.set('metadata', metadata)
      this.files.set(id, entry)
    }

    const yDoc = new Y.Doc()
    const provider = this.createWebsocketProvider(
      this.getSocketUrl('code'),
      id,
      yDoc
    )
    this.initProvider(provider, id)

    const content = yDoc.getText()
    const record = this.files.get(id)
    const cmDoc = CodeMirror.Doc(
      content.toString(),
      record.get('metadata').get('mode')
    )

    if (observers?.metadata) {
      observers.metadata(record.get('metadata').toJSON())
      record
        .get('metadata')
        .observe(() => observers.metadata(record.get('metadata').toJSON()))
    }

    this.editors.code.container.swapDoc(cmDoc)
    this.editors.code.provider = provider
    this.editors.code.binding = new CodeMirrorBinding(
      content,
      this.editors.code.container,
      provider.awareness
    )
  }

  maximize = (type) => {
    this.doc.transact(() => {
      this.panels
        .toArray()
        .filter((v) => v !== type)
        .forEach(this.closePanel)
      this.fullscreen.delete(0, this.fullscreen.length)
      this.fullscreen.insert(0, type)
    })
  }

  minimize = (type) => {
    if (type !== this.fullscreen.toString()) return
    this.fullscreen.delete(0, this.fullscreen.length)
  }

  // TODO
  // unobserve on unmounts
  // since we use setState
  observe = (field, cb) => {
    this.observerProxy.observe(field, cb)

    switch (field) {
      case 'panels':
        this.panels.observe(cb)
        break
      case 'participants':
        this.participants?.observe(cb)
        break
      case 'fullscreen':
        this.fullscreen.observe(cb)
        break
      case 'frames.code.tabs':
        this.frames.get('code').get('tabs').observeDeep(cb)
        break
      case 'frames.code.active':
        this.frames.get('code').observe(cb)
        break
      case 'frames.note.tabs':
        this.frames.get('note').get('tabs').observeDeep(cb)
        break
      case 'frames.note.active':
        this.frames.get('note').observe(cb)
        break
      case 'frames.sketch.tabs':
        this.frames.get('sketch').get('tabs').observeDeep(cb)
        break
      case 'frames.sketch.active':
        this.cb = cb
        this.frames.get('sketch').observe(cb)
        break
      case 'frames.finder.active':
        this.frames.get('finder').observe(cb)
        break
      case 'frames':
        this.frames.observeDeep(cb)
        break
      case 'bindings:sketch:awareness':
        if (!this.observers['bindings:sketch:awareness'])
          this.observers['bindings:sketch:awareness'] = []
        this.observers['bindings:sketch:awareness'].push(cb)
        break
      default:
        break
    }
  }

  unobserve = (field, cb) => {
    if (!this.observerProxy.has(field, cb)) return

    this.observerProxy.unobserve(field, cb)

    switch (field) {
      case 'panels':
        this.panels.unobserve(cb)
        break
      case 'participants':
        this.participants?.unobserve(cb)
        break
      case 'fullscreen':
        this.fullscreen.unobserve(cb)
        break
      case 'frames.code.tabs':
        this.frames.get('code').get('tabs').unobserveDeep(cb)
        break
      case 'frames.code.active':
        this.frames.get('code').unobserve(cb)
        break
      case 'frames.note.tabs':
        this.frames.get('note').get('tabs').unobserveDeep(cb)
        break
      case 'frames.note.active':
        this.frames.get('note').unobserve(cb)
        break
      case 'frames.sketch.tabs':
        this.frames.get('sketch').get('tabs').unobserveDeep(cb)
        break
      case 'frames.sketch.active':
        this.frames.get('sketch').unobserve(cb)
        break
      case 'frames.finder.active':
        this.frames.get('finder').unobserve(cb)
        break
      case 'frames':
        this.frames.unobserveDeep(cb)
        break
      case 'bindings:sketch:awareness':
        this.observers['bindings:sketch:awareness'] = this.observers[
          'bindings:sketch:awareness'
        ].filter((ob) => ob !== cb)
        break
      default:
        break
    }
  }

  observeMany = (observerMap) => {
    Object.keys(observerMap).forEach((key) => {
      observerMap[key].forEach((observer) => {
        try {
          observer()
        } catch (e) {
          console.error(e)
        }
        this.observe(key, observer)
      })
    })
  }

  unobserveAll = () => {
    const observerMap = this.observerProxy.getObservers()

    Object.keys(observerMap).forEach((key) => {
      observerMap[key].forEach((observer) => {
        this.unobserve(key, observer)
      })
    })

    return observerMap
  }

  getPanels = () => this.sortPanels(this.panels.toArray())

  getTabs = (type) => {
    const tabs = this.frames
      .get(type)
      .get('tabs')
      .toArray()
      .reduce((acc, tab) => {
        return {
          ...acc,
          [tab.get('id').toString()]: {
            status: 'IDLE',
            data: {
              id: tab.get('id'),
              name: tab.get('name')
            }
          }
        }
      }, {})

    return tabs
  }

  getActive = (type) => {
    return this.frames.get(type).get('active') || undefined
  }

  getActiveMetadata = (type) => {
    const fileId = this.getActive(type)
    return fileId ? this.files.get(fileId).get('metadata') : {}
  }

  getFullscreen = () => {
    return this.fullscreen.toString()
  }

  getBinding = (type) => {
    return this.editors[type]?.binding
  }

  getEditor = (type) => {
    return this.editors[type]?.container
  }

  getSocketUrl = (type) => {
    /**
     * Chiron accepts:
     *   '/classroom/room/:userId' -- playground
     *   '/classroom/room/:apptId'
     *   '/classroom/room/:apptId/file/:fileId'
     *
     * the last `id` ommited so that this works:
     * @ref https://github.com/yjs/y-websocket/blob/master/src/y-websocket.js#L191
     */
    const base = process.env.LYCEUM_SOCKET_URL

    switch (type) {
      case 'root':
        return `${base}/classroom`
      case 'sketch':
      case 'code':
      case 'note':
        return `${base}/classroom/${this.classroomId}/file`
      default:
        console.warn('Unknown socket type')
    }
  }

  isSynced = () => this.synced

  createWebsocketProvider = (url, id, doc) => {
    if (this.env === ClassroomConstants.ENV.RECORDING)
      return new RecordingSocketProvider(url, id, doc)

    return new WebsocketProvider(url, id, doc, {
      params: {
        token: this.token,
        env: this.env
      },
      disableBc: true
    })
  }

  getProviders() {
    return [
      this.provider,
      this.editors.sketch?.provider,
      this.editors.note?.provider,
      this.editors.code?.provider
    ]
  }

  getProvider(id) {
    return this.getProviders().find((p) => p?.id === id)
  }

  getProviderByType(type) {
    switch (type) {
      case 'root':
        return this.provider
      case 'sketch':
      case 'note':
      case 'code':
        return this.editors[type]?.provider
      default:
        console.error('Invalid provider type', type)
    }
  }

  applyUpdates = (updates) => {
    const grouppedUpdates = groupBy(updates, (update) => update.doc)

    Object.entries(grouppedUpdates).forEach(([docId, docUpdates]) => {
      const doc = this.getProvider(docId)?.doc

      if (!doc) {
        console.error(`Update sent to a non-existing provider ${docId}`)
        return
      }

      doc.transact(() => {
        docUpdates.forEach((docUpdate) => {
          Y.applyUpdate(doc, docUpdate.payload)
        })
      })
    })
  }

  log(...args) {
    if (this.debug) {
      console.log(...args)
    }
  }
}

const cm = new ClassroomManager()

export default cm
